"use client";

import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  createInitiativeAction,
  updateInitiativeAction,
  deleteInitiativeAction,
  createIdeaAction,
  updateIdeaAction,
  deleteIdeaAction,
} from "@/actions/ops-actions";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  Search,
  Plus,
  Trash2,
  Edit2,
  Lightbulb,
  ArrowRightLeft,
  Calendar,
  User,
  ArrowRight,
  TrendingUp,
} from "lucide-react";

interface AgentItem {
  id: string;
  name: string;
}

interface IdeaItem {
  id: string;
  title: string;
  description: string;
  createdAt: string;
  authorName: string | null;
  authorId: string | null;
}

interface InitiativeItem {
  id: string;
  title: string;
  status:
    "idea" | "proposal" | "evaluation" | "experiment" | "approved" | "rejected";
  createdAt: string;
  authorName: string | null;
  authorId: string | null;
}

interface InitiativesClientProps {
  initialIdeas: IdeaItem[];
  initialInitiatives: InitiativeItem[];
  agents: AgentItem[];
}

const PIPELINE_COLUMNS = [
  { id: "idea", name: "Idea", color: "neutral", bg: "bg-card-muted/50" },
  {
    id: "proposal",
    name: "Proposta",
    color: "warning",
    bg: "bg-warning-soft/10",
  },
  {
    id: "evaluation",
    name: "Valutazione",
    color: "info",
    bg: "bg-info-soft/10",
  },
  {
    id: "experiment",
    name: "Esperimento",
    color: "primary",
    bg: "bg-primary-50/10",
  },
  {
    id: "approved",
    name: "Approvata",
    color: "success",
    bg: "bg-success-soft/10",
  },
  { id: "rejected", name: "Rifiutata", color: "error", bg: "bg-error-soft/10" },
];

export function InitiativesClient({
  initialIdeas,
  initialInitiatives,
  agents,
}: InitiativesClientProps) {
  const router = useRouter();
  const [ideas, setIdeas] = useState<IdeaItem[]>(initialIdeas);
  const [initiatives, setInitiatives] =
    useState<InitiativeItem[]>(initialInitiatives);

  const [activeTab, setActiveTab] = useState<"ideas" | "pipeline">("pipeline");
  const [search, setSearch] = useState("");

  // Modals state
  const [isIdeaModalOpen, setIsIdeaModalOpen] = useState(false);
  const [isInitModalOpen, setIsInitModalOpen] = useState(false);
  const [editingIdea, setEditingIdea] = useState<IdeaItem | null>(null);
  const [editingInit, setEditingInit] = useState<InitiativeItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form states - Idea
  const [ideaTitle, setIdeaTitle] = useState("");
  const [ideaDesc, setIdeaDesc] = useState("");
  const [ideaAuthor, setIdeaAuthor] = useState("");

  // Form states - Initiative
  const [initTitle, setInitTitle] = useState("");
  const [initStatus, setInitStatus] =
    useState<InitiativeItem["status"]>("idea");
  const [initAuthor, setInitAuthor] = useState("");

  useEffect(() => {
    setIdeas(initialIdeas);
  }, [initialIdeas]);

  useEffect(() => {
    setInitiatives(initialInitiatives);
  }, [initialInitiatives]);

  // --- Ideas Handlers ---
  const handleOpenIdeaAddModal = () => {
    setEditingIdea(null);
    setIdeaTitle("");
    setIdeaDesc("");
    setIdeaAuthor("");
    setError("");
    setIsIdeaModalOpen(true);
  };

  const handleOpenIdeaEditModal = (idea: IdeaItem) => {
    setEditingIdea(idea);
    setIdeaTitle(idea.title);
    setIdeaDesc(idea.description);
    setIdeaAuthor(idea.authorId || "");
    setError("");
    setIsIdeaModalOpen(true);
  };

  const handleIdeaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!ideaTitle.trim()) {
      setError("Il titolo dell'idea è obbligatorio.");
      return;
    }
    if (!ideaDesc.trim()) {
      setError("La descrizione dell'idea è obbligatoria.");
      return;
    }

    setLoading(true);
    try {
      if (editingIdea) {
        const res = await updateIdeaAction({
          id: editingIdea.id,
          title: ideaTitle,
          description: ideaDesc,
          authorId: ideaAuthor || null,
        });
        if (res.error) setError(res.error);
        else {
          setIsIdeaModalOpen(false);
          router.refresh();
        }
      } else {
        const res = await createIdeaAction({
          title: ideaTitle,
          description: ideaDesc,
          authorId: ideaAuthor || null,
        });
        if (res.error) setError(res.error);
        else {
          setIsIdeaModalOpen(false);
          router.refresh();
        }
      }
    } catch (err: any) {
      setError(err.message || "Errore durante il salvataggio dell'idea.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteIdea = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questa idea?")) return;

    try {
      const res = await deleteIdeaAction(id);
      if (res.error) alert(res.error);
      else router.refresh();
    } catch (err) {
      alert("Errore durante l'eliminazione.");
    }
  };

  const handlePromoteIdea = async (idea: IdeaItem) => {
    if (
      !confirm(
        `Vuoi promuovere l'idea "${idea.title}" a Iniziativa formale nella pipeline?`,
      )
    )
      return;

    setLoading(true);
    try {
      // 1. Create the formal initiative
      const resInit = await createInitiativeAction({
        title: idea.title,
        status: "proposal", // Start as Proposal
        authorId: idea.authorId,
      });

      if (resInit.error) {
        alert(resInit.error);
      } else {
        // 2. Remove the raw idea to clean the backlog
        await deleteIdeaAction(idea.id);
        setActiveTab("pipeline");
        router.refresh();
      }
    } catch (err) {
      alert("Errore durante la promozione dell'idea.");
    } finally {
      setLoading(false);
    }
  };

  // --- Initiatives Handlers ---
  const handleOpenInitAddModal = () => {
    setEditingInit(null);
    setInitTitle("");
    setInitStatus("idea");
    setInitAuthor("");
    setError("");
    setIsInitModalOpen(true);
  };

  const handleOpenInitEditModal = (init: InitiativeItem) => {
    setEditingInit(init);
    setInitTitle(init.title);
    setInitStatus(init.status);
    setInitAuthor(init.authorId || "");
    setError("");
    setIsInitModalOpen(true);
  };

  const handleInitSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!initTitle.trim()) {
      setError("Il titolo dell'iniziativa è obbligatorio.");
      return;
    }

    setLoading(true);
    try {
      if (editingInit) {
        const res = await updateInitiativeAction({
          id: editingInit.id,
          title: initTitle,
          status: initStatus,
          authorId: initAuthor || null,
        });
        if (res.error) setError(res.error);
        else {
          setIsInitModalOpen(false);
          router.refresh();
        }
      } else {
        const res = await createInitiativeAction({
          title: initTitle,
          status: initStatus,
          authorId: initAuthor || null,
        });
        if (res.error) setError(res.error);
        else {
          setIsInitModalOpen(false);
          router.refresh();
        }
      }
    } catch (err: any) {
      setError(err.message || "Errore durante il salvataggio dell'iniziativa.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id: string, newStatus: any) => {
    const init = initiatives.find((i) => i.id === id);
    if (!init) return;

    try {
      const res = await updateInitiativeAction({
        id,
        title: init.title,
        status: newStatus,
        authorId: init.authorId,
      });

      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err) {
      alert("Errore nell'aggiornamento dello stato.");
    }
  };

  const handleDeleteInit = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questa iniziativa?")) return;

    try {
      const res = await deleteInitiativeAction(id);
      if (res.error) alert(res.error);
      else router.refresh();
    } catch (err) {
      alert("Errore durante l'eliminazione.");
    }
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  };

  const filteredIdeas = ideas.filter(
    (idea) =>
      idea.title.toLowerCase().includes(search.toLowerCase()) ||
      idea.description.toLowerCase().includes(search.toLowerCase()),
  );

  const filteredInitiatives = initiatives.filter((init) =>
    init.title.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      {/* Switcher & Tabs */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex bg-card p-1 rounded-xl border border-border w-full sm:w-auto shrink-0 shadow-sm">
          <button
            onClick={() => setActiveTab("pipeline")}
            className={`flex-1 sm:flex-none px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
              activeTab === "pipeline"
                ? "bg-primary-500 text-text-on-primary shadow-sm"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            🚀 Pipeline Iniziative
          </button>
          <button
            onClick={() => setActiveTab("ideas")}
            className={`flex-1 sm:flex-none px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
              activeTab === "ideas"
                ? "bg-primary-500 text-text-on-primary shadow-sm"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            💡 Brainstorming & Idee
          </button>
        </div>

        {/* Action Button & Search */}
        <div className="flex w-full sm:w-auto gap-3 items-center">
          <div className="relative flex-1 sm:w-60">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
            <Input
              placeholder={
                activeTab === "pipeline"
                  ? "Cerca iniziative..."
                  : "Cerca idee..."
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {activeTab === "pipeline" ? (
            <Button
              onClick={handleOpenInitAddModal}
              className="gap-1.5 shrink-0"
            >
              <Plus className="h-4 w-4" />
              Nuova Iniziativa
            </Button>
          ) : (
            <Button
              onClick={handleOpenIdeaAddModal}
              className="gap-1.5 shrink-0 bg-success hover:bg-success/90 border-none"
            >
              <Plus className="h-4 w-4" />
              Nuova Idea
            </Button>
          )}
        </div>
      </div>

      {/* TABS CONTAINER */}
      {activeTab === "pipeline" ? (
        /* KANBAN PIPELINE VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-start">
          {PIPELINE_COLUMNS.map((col) => {
            const colInits = filteredInitiatives.filter(
              (i) => i.status === col.id,
            );
            return (
              <div
                key={col.id}
                className={`flex flex-col gap-3 rounded-xl border border-border/60 p-3 ${col.bg} min-h-[450px]`}
              >
                {/* Column Header */}
                <div className="flex items-center justify-between pb-2 border-b border-border/40">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-bold text-xs text-text-primary uppercase tracking-wider truncate">
                      {col.name}
                    </span>
                    <Badge
                      variant={col.color as any}
                      className="text-[10px] px-1.5 py-0.5"
                    >
                      {colInits.length}
                    </Badge>
                  </div>
                </div>

                {/* Column Items */}
                <div className="space-y-3 flex-1 overflow-y-auto max-h-[500px] pr-1">
                  {colInits.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-6 border border-dashed border-border/30 rounded-lg bg-card/10 text-center text-[10px] text-text-muted">
                      Vuota
                    </div>
                  ) : (
                    colInits.map((init) => (
                      <Card
                        key={init.id}
                        className="relative hover:border-primary-500/50 transition-all group shadow-sm bg-card cursor-pointer"
                        onClick={() => handleOpenInitEditModal(init)}
                      >
                        {/* Hover Actions */}
                        <div
                          className="absolute top-2.5 right-2.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Link
                            href={`/explore?nodeId=${init.id}`}
                            title="Esplora relazioni"
                          >
                            <TrendingUp className="h-3 w-3 text-text-secondary hover:text-primary-600 cursor-pointer" />
                          </Link>
                          <button
                            onClick={() => handleOpenInitEditModal(init)}
                            title="Modifica"
                            className="text-text-secondary hover:text-primary-600 cursor-pointer"
                          >
                            <Edit2 className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleDeleteInit(init.id)}
                            title="Elimina"
                            className="text-text-secondary hover:text-error cursor-pointer"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>

                        <CardHeader className="p-3 pb-2 pr-16 space-y-1">
                          <h4 className="font-bold text-xs text-text-primary leading-snug break-words">
                            <Link
                              href={`/explore?nodeId=${init.id}`}
                              className="hover:text-primary-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {init.title}
                            </Link>
                          </h4>
                        </CardHeader>

                        <CardContent className="p-3 pt-0 pb-2 space-y-1.5 text-[10px] text-text-secondary">
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-text-muted shrink-0" />
                            <span className="truncate">
                              {init.authorName || "Nessuno"}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-text-muted shrink-0" />
                            <span>Creata il: {formatDate(init.createdAt)}</span>
                          </div>
                        </CardContent>

                        <CardFooter
                          className="p-2 pt-2 border-t border-border/30 flex justify-end items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Status Shift Select */}
                          <div className="relative shrink-0 flex items-center gap-0.5">
                            <ArrowRightLeft className="h-2.5 w-2.5 text-text-muted" />
                            <select
                              value={init.status}
                              onChange={(e) =>
                                handleStatusChange(
                                  init.id,
                                  e.target.value as any,
                                )
                              }
                              className="text-[9px] font-semibold text-text-secondary bg-transparent border-0 outline-none cursor-pointer focus:ring-0 pr-1 hover:text-primary-600 appearance-none"
                            >
                              <option value="idea">Idea</option>
                              <option value="proposal">Proposta</option>
                              <option value="evaluation">Valutazione</option>
                              <option value="experiment">Esperimento</option>
                              <option value="approved">Approvata</option>
                              <option value="rejected">Rifiutata</option>
                            </select>
                          </div>
                        </CardFooter>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* BRAINSTORMING BACKLOG VIEW */
        <div>
          {filteredIdeas.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/50 text-center max-w-xl mx-auto my-8">
              <Lightbulb className="h-10 w-10 text-success mb-3 animate-pulse" />
              <h3 className="text-base font-bold text-text-primary">
                Nessuna idea inserita
              </h3>
              <p className="text-sm text-text-secondary mt-1">
                La scatola delle idee è vuota. Raccogli e scrivi qualsiasi
                spunto o suggerimento operativo qui.
              </p>
              <Button
                onClick={handleOpenIdeaAddModal}
                className="mt-4 gap-1.5 bg-success hover:bg-success/90 border-none"
              >
                <Plus className="h-4 w-4" />
                Crea Prima Idea
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredIdeas.map((idea) => (
                <Card
                  key={idea.id}
                  className="relative overflow-hidden flex flex-col justify-between hover:border-success/50 transition-all border-t-4 border-t-success group cursor-pointer"
                  onClick={() => handleOpenIdeaEditModal(idea)}
                >
                  {/* Hover Actions */}
                  <div
                    className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Link
                      href={`/explore?nodeId=${idea.id}`}
                      title="Esplora relazioni"
                    >
                      <Button
                        variant="secondary"
                        size="icon"
                        className="h-7 w-7 p-0 flex items-center justify-center"
                      >
                        <TrendingUp className="h-3.5 w-3.5 text-text-secondary hover:text-success" />
                      </Button>
                    </Link>
                    <Button
                      variant="secondary"
                      size="icon"
                      onClick={() => handleOpenIdeaEditModal(idea)}
                      title="Modifica"
                      className="h-7 w-7 p-0 flex items-center justify-center"
                    >
                      <Edit2 className="h-3.5 w-3.5 text-text-secondary hover:text-success" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="icon"
                      onClick={() => handleDeleteIdea(idea.id)}
                      title="Elimina"
                      className="h-7 w-7 p-0 flex items-center justify-center hover:bg-error-soft hover:text-error"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-text-secondary" />
                    </Button>
                  </div>

                  <CardHeader className="pb-2 pr-24">
                    <div className="flex justify-between items-start gap-4">
                      <h3 className="font-bold text-base text-text-primary leading-snug">
                        <Link
                          href={`/explore?nodeId=${idea.id}`}
                          className="hover:text-success hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {idea.title}
                        </Link>
                      </h3>
                      <Lightbulb className="h-5 w-5 text-success shrink-0" />
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pb-3 flex-1">
                    <p className="text-xs text-text-secondary whitespace-pre-wrap leading-relaxed line-clamp-4">
                      {idea.description}
                    </p>

                    <div
                      className="flex items-center justify-between text-[10px] text-text-muted pt-2 border-t border-border/30"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-1">
                        <User className="h-3.5 w-3.5 text-text-muted" />
                        <span>
                          Saggio di:{" "}
                          <strong>{idea.authorName || "Anonimo"}</strong>
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 text-text-muted" />
                        <span>
                          {new Date(idea.createdAt).toLocaleDateString("it-IT")}
                        </span>
                      </div>
                    </div>
                  </CardContent>

                  <CardFooter
                    className="flex justify-end items-center bg-card-muted/20 px-6 py-3 border-t border-border/20 text-xs shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      size="sm"
                      onClick={() => handlePromoteIdea(idea)}
                      className="h-8 px-3 gap-1 bg-success hover:bg-success/90 text-text-on-primary text-xs border-none"
                      loading={loading}
                    >
                      Promuovi
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* --- ADD / EDIT IDEA DIALOG --- */}
      <Dialog open={isIdeaModalOpen} onOpenChange={setIsIdeaModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingIdea ? "Modifica Idea" : "Nuovo Spunto di Brainstorming"}
            </DialogTitle>
            <DialogDescription>
              Scrivi un'idea o una bozza di proposta. Potrà essere elevata a
              Iniziativa formale in qualsiasi momento.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleIdeaSubmit} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="idea-title">Titolo dell'Idea</Label>
              <Input
                id="idea-title"
                placeholder="es. Transizione flotta aziendale verso elettrico"
                value={ideaTitle}
                onChange={(e) => setIdeaTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="idea-author">Proponente / Autore</Label>
              <Select
                id="idea-author"
                value={ideaAuthor}
                onChange={(e) => setIdeaAuthor(e.target.value)}
                disabled={loading}
              >
                <option value="">Anonimo</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="idea-desc">Descrizione & Motivazioni</Label>
              <textarea
                id="idea-desc"
                placeholder="Fornisci dettagli sul perché questa idea sia interessante, sul possibile impatto e sui requisiti iniziali..."
                value={ideaDesc}
                onChange={(e) => setIdeaDesc(e.target.value)}
                disabled={loading}
                rows={4}
                required
                className="flex w-full rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground resize-none"
              />
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsIdeaModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                loading={loading}
                className="bg-success hover:bg-success/90 border-none"
              >
                {editingIdea ? "Salva Idea" : "Aggiungi Idea"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* --- ADD / EDIT INITIATIVE DIALOG --- */}
      <Dialog open={isInitModalOpen} onOpenChange={setIsInitModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingInit ? "Modifica Iniziativa" : "Crea Nuova Iniziativa"}
            </DialogTitle>
            <DialogDescription>
              Inserisci o aggiorna i dettagli dell'iniziativa formale
              all'interno della pipeline di innovazione.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleInitSubmit} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="init-title">Titolo Iniziativa</Label>
              <Input
                id="init-title"
                placeholder="es. Automazione fatturazione tramite IA"
                value={initTitle}
                onChange={(e) => setInitTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="init-status">Stato della Iniziativa</Label>
                <Select
                  id="init-status"
                  value={initStatus}
                  onChange={(e) => setInitStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="idea">Idea (Backlog)</option>
                  <option value="proposal">Proposta (Proposal)</option>
                  <option value="evaluation">Valutazione (Evaluation)</option>
                  <option value="experiment">Esperimento (Experiment)</option>
                  <option value="approved">Approvata (Approved)</option>
                  <option value="rejected">Rifiutata (Rejected)</option>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="init-author">Proprietario / Autore</Label>
                <Select
                  id="init-author"
                  value={initAuthor}
                  onChange={(e) => setInitAuthor(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Nessuno</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsInitModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                {editingInit ? "Salva Modifiche" : "Crea Iniziativa"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
